import {
	Check,
	Copy,
	Key,
	RefreshCw,
	Shield,
	ShieldCheck,
	ShieldOff,
} from "lucide-react";
import QRCode from "qrcode";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient, useSession } from "@/lib/auth-client";
import { BADGE_TONES } from "@/lib/badge-tones";
import { getSafePostAuthRedirectTarget } from "@/lib/two-factor-policy";

type TwoFactorStep = "idle" | "setup" | "backup-codes";

export default function AdminSecurityPage() {
	const { data: session } = useSession();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const redirectTarget = React.useMemo(
		() => getSafePostAuthRedirectTarget(searchParams.get("redirect")),
		[searchParams],
	);
	const isRequiredForAccess = searchParams.get("required") === "1";

	const initialTwoFactorEnabled = Boolean(
		(session?.user as { twoFactorEnabled?: boolean } | undefined)
			?.twoFactorEnabled,
	);

	const [localTwoFactorOverride, setLocalTwoFactorOverride] = React.useState<
		boolean | null
	>(null);
	const twoFactorEnabled = localTwoFactorOverride ?? initialTwoFactorEnabled;

	const [step, setStep] = React.useState<TwoFactorStep>("idle");
	const [password, setPassword] = React.useState("");
	const [verificationCode, setVerificationCode] = React.useState("");
	const [totpUri, setTotpUri] = React.useState("");
	const [totpQrDataUrl, setTotpQrDataUrl] = React.useState<string | null>(null);
	const [backupCodes, setBackupCodes] = React.useState<string[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [success, setSuccess] = React.useState<string | null>(null);
	const [copiedCodes, setCopiedCodes] = React.useState(false);

	React.useEffect(() => {
		if (!totpUri) {
			setTotpQrDataUrl(null);
			return;
		}

		let cancelled = false;

		const generate = async () => {
			try {
				const dataUrl = await QRCode.toDataURL(totpUri);
				if (!cancelled) {
					setTotpQrDataUrl(dataUrl);
				}
			} catch {
				if (!cancelled) {
					setTotpQrDataUrl(null);
				}
			}
		};

		void generate();

		return () => {
			cancelled = true;
		};
	}, [totpUri]);

	const resetMessages = React.useCallback(() => {
		setError(null);
		setSuccess(null);
	}, []);

	const resetState = React.useCallback(() => {
		setStep("idle");
		setPassword("");
		setVerificationCode("");
		setTotpUri("");
		setTotpQrDataUrl(null);
		setBackupCodes([]);
		setCopiedCodes(false);
		resetMessages();
	}, [resetMessages]);

	const startSetup = React.useCallback(async () => {
		resetMessages();

		if (!password) {
			setError("Please enter your password");
			return;
		}

		setLoading(true);
		try {
			const result = await authClient.twoFactor.enable({ password });

			if (result.error) {
				setError(result.error.message || "Failed to enable 2FA");
				return;
			}

			if (result.data) {
				setTotpUri(result.data.totpURI);
				setBackupCodes(result.data.backupCodes);
				setStep("setup");
			}
		} catch {
			setError("An error occurred while enabling 2FA");
		} finally {
			setLoading(false);
		}
	}, [password, resetMessages]);

	const verifySetup = React.useCallback(async () => {
		resetMessages();

		if (!verificationCode || verificationCode.length !== 6) {
			setError("Please enter a valid 6-digit code");
			return;
		}

		setLoading(true);
		try {
			const result = await authClient.twoFactor.verifyTotp({
				code: verificationCode,
			});

			if (result.error) {
				setError(result.error.message || "Invalid verification code");
				return;
			}

			setLocalTwoFactorOverride(true);
			setStep("backup-codes");
			setSuccess("Two-factor authentication enabled successfully");
		} catch {
			setError("An error occurred during verification");
		} finally {
			setLoading(false);
		}
	}, [verificationCode, resetMessages]);

	const regenerateBackupCodes = React.useCallback(async () => {
		resetMessages();

		if (!password) {
			setError("Please enter your password");
			return;
		}

		setLoading(true);
		try {
			const result = await authClient.twoFactor.generateBackupCodes({
				password,
			});

			if (result.error) {
				setError(result.error.message || "Failed to generate backup codes");
				return;
			}

			if (result.data) {
				setBackupCodes(result.data.backupCodes);
				setStep("backup-codes");
				setSuccess("New backup codes generated");
			}
		} catch {
			setError("An error occurred while generating backup codes");
		} finally {
			setLoading(false);
		}
	}, [password, resetMessages]);

	const copyBackupCodes = React.useCallback(async () => {
		await navigator.clipboard.writeText(backupCodes.join("\n"));
		setCopiedCodes(true);
		setTimeout(() => setCopiedCodes(false), 2000);
	}, [backupCodes]);

	const manualSecret = React.useMemo(() => {
		const secretPart = totpUri.split("secret=")[1];
		if (!secretPart) return "";
		return secretPart.split("&")[0] ?? "";
	}, [totpUri]);

	const waitForTwoFactorSession = React.useCallback(async () => {
		const maxWaitMs = 3000;
		const pollIntervalMs = 100;
		const startTime = Date.now();

		while (Date.now() - startTime < maxWaitMs) {
			const nextSession = await authClient.getSession();
			if (
				(nextSession.data?.user as { twoFactorEnabled?: boolean } | undefined)
					?.twoFactorEnabled === true
			) {
				return;
			}
			await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
		}
	}, []);

	const handleContinue = React.useCallback(async () => {
		await waitForTwoFactorSession();
		navigate(redirectTarget);
	}, [navigate, redirectTarget, waitForTwoFactorSession]);

	const handleBackupCodesDone = React.useCallback(async () => {
		if (isRequiredForAccess) {
			await waitForTwoFactorSession();
			resetState();
			navigate(redirectTarget);
			return;
		}

		resetState();
	}, [
		isRequiredForAccess,
		navigate,
		redirectTarget,
		resetState,
		waitForTwoFactorSession,
	]);

	return (
		<div className="space-y-8">
			<div className="space-y-2">
				<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
					Account security
				</p>
				<div className="flex items-start gap-3">
					<Shield className="mt-0.5 h-6 w-6 text-primary" />
					<div className="space-y-1">
						<h1 className="text-2xl font-semibold tracking-tight">
							Security settings
						</h1>
						<p className="max-w-2xl text-xs text-muted-foreground">
							Manage your account security posture and two-factor authentication from the same surface.
						</p>
					</div>
				</div>
			</div>

			{isRequiredForAccess && !twoFactorEnabled ? (
				<div className="max-w-5xl rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
					Two-factor authentication is required for all internal users. Finish
					setup to continue.
				</div>
			) : null}

			<Card className="max-w-5xl">
				<CardHeader>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							{twoFactorEnabled ? (
								<ShieldCheck className="h-6 w-6 text-green-500" />
							) : (
								<ShieldOff className="h-6 w-6 text-muted-foreground" />
							)}
							<div>
								<CardTitle>Two-Factor Authentication</CardTitle>
								<CardDescription>
									{twoFactorEnabled
										? "Your account is protected with 2FA"
										: "Add an extra layer of security to your account"}
								</CardDescription>
							</div>
						</div>

						<div>
							{twoFactorEnabled ? (
								<Badge
									variant="outline"
									className={`${BADGE_TONES.emerald} px-2 py-1 text-sm`}
								>
									Enabled
								</Badge>
							) : (
								<Badge
									variant="outline"
									className={`${BADGE_TONES.slate} px-2 py-1 text-sm`}
								>
									Disabled
								</Badge>
							)}
						</div>
					</div>
				</CardHeader>

				<CardContent className="space-y-4">
					{error && (
						<div className="rounded-md bg-destructive/15 p-3 text-destructive text-sm">
							{error}
						</div>
					)}
					{success && (
						<div className="rounded-md bg-emerald-500/15 p-3 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 text-sm">
							{success}
						</div>
					)}

					{step === "idle" && !twoFactorEnabled && (
						<div className="space-y-4">
							<p className="text-muted-foreground text-sm">
								{isRequiredForAccess
									? "Two-factor authentication is required before you can continue using the internal app."
									: "Add an extra verification step in addition to your password when signing in."}
							</p>
							<div className="space-y-2">
								<Label htmlFor="setup-password">
									Enter your password to enable 2FA
								</Label>
								<Input
									id="setup-password"
									type="password"
									value={password}
									onChange={(event) => setPassword(event.target.value)}
									placeholder="Your password"
									disabled={loading}
								/>
							</div>
							<Button onClick={startSetup} disabled={loading || !password}>
								{loading ? "Enabling..." : "Enable Two-Factor Authentication"}
							</Button>
						</div>
					)}

					{step === "idle" && twoFactorEnabled && (
						<div className="space-y-4">
							<p className="text-muted-foreground text-sm">
								Two-factor authentication is active and required for all
								internal users. You can regenerate backup codes anytime.
							</p>

							<div className="flex flex-wrap gap-2">
								<div className="flex min-w-[280px] gap-2">
									<Input
										type="password"
										value={password}
										onChange={(event) => setPassword(event.target.value)}
										placeholder="Password to regenerate codes"
										disabled={loading}
									/>
									<Button
										variant="outline"
										onClick={regenerateBackupCodes}
										disabled={loading || !password}
									>
										<RefreshCw className="mr-2 h-4 w-4" />
										Regenerate
									</Button>
								</div>

								{isRequiredForAccess ? (
									<Button onClick={handleContinue}>Continue</Button>
								) : null}
							</div>
						</div>
					)}

					{step === "setup" && (
						<div className="space-y-4">
							<div className="space-y-4 text-center">
								<h3 className="font-semibold">Scan QR Code</h3>
								<p className="text-muted-foreground text-sm">
									Scan with your authenticator app (Google Authenticator, Authy,
									1Password, etc.)
								</p>

								<div className="flex justify-center rounded-xl border bg-muted/20 p-4">
									<div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-950/5">
										{totpQrDataUrl ? (
											<img
												src={totpQrDataUrl}
												alt="QR Code for 2FA setup"
												className="h-48 w-48"
											/>
										) : (
											<div className="flex h-48 w-48 items-center justify-center text-muted-foreground text-sm">
												Loading...
											</div>
										)}
									</div>
								</div>

								<p className="text-muted-foreground text-xs">
									Manual code:{" "}
									<code className="rounded bg-muted px-1 py-0.5 text-xs break-all">
										{manualSecret}
									</code>
								</p>
							</div>

							<div className="space-y-2">
								<Label htmlFor="verify-code">
									Enter 6-digit code from your authenticator app
								</Label>
								<Input
									id="verify-code"
									type="text"
									value={verificationCode}
									onChange={(event) => setVerificationCode(event.target.value)}
									placeholder="000000"
									maxLength={6}
									disabled={loading}
									className="text-center text-lg tracking-widest"
								/>
							</div>

							<div className="flex gap-2">
								<Button variant="outline" onClick={resetState}>
									Cancel
								</Button>
								<Button
									onClick={verifySetup}
									disabled={loading || verificationCode.length !== 6}
								>
									{loading ? "Verifying..." : "Verify and Enable"}
								</Button>
							</div>
						</div>
					)}

					{step === "backup-codes" && (
						<div className="space-y-4">
							<div className="space-y-2 text-center">
								<Key className="mx-auto h-12 w-12 text-primary" />
								<h3 className="font-semibold">Save Your Backup Codes</h3>
								<p className="text-muted-foreground text-sm">
									Use these one-time codes if you lose access to your
									authenticator app.
								</p>
							</div>

							<div className="rounded-lg bg-muted p-4">
								<div className="grid grid-cols-2 gap-2">
									{backupCodes.map((code, index) => (
										<code
											key={`${code}-${index}`}
											className="rounded bg-background p-2 text-center font-mono text-sm"
										>
											{code}
										</code>
									))}
								</div>
							</div>

							<div className="flex justify-center gap-2">
								<Button
									variant="outline"
									onClick={() => void copyBackupCodes()}
								>
									{copiedCodes ? (
										<>
											<Check className="mr-2 h-4 w-4" />
											Copied!
										</>
									) : (
										<>
											<Copy className="mr-2 h-4 w-4" />
											Copy Codes
										</>
									)}
								</Button>
								<Button onClick={handleBackupCodesDone}>
									{isRequiredForAccess ? "Continue" : "Done"}
								</Button>
							</div>

							<p className="text-center text-destructive text-xs">
								Store these codes in a safe place. You will not be able to view
								them again.
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
